// Google Analytics 4 (gtag) + Meta (Facebook) Pixel - CNIL-compliant
// "consent-first" loading. No tracking script is loaded until the user has
// explicitly accepted.

import { isBrowser, safeLocalStorage } from '~/utils/safeWindow';

const CONSENT_KEY = 'ga-consent';
export type Consent = 'granted' | 'denied';

// Event used to reopen the consent banner from anywhere
// (e.g. a "Manage cookies" link in the footer).
export const COOKIE_BANNER_EVENT = 'open-cookie-banner';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    // Meta Pixel global. Typed loosely, matching Meta's official snippet.
    fbq?: any;
    _fbq?: any;
  }
}


export const isAnalyticsConfigured = (): boolean => process.env.GATSBY_GOOGLE_ANALYTICS_ID !== undefined;

export const isPixelConfigured = (): boolean => process.env.GATSBY_FACEBOOK_PIXEL_ID !== undefined;

// Whether any tracking tool is configured (drives the consent banner display).
export const isTrackingConfigured = (): boolean =>
  isAnalyticsConfigured() || isPixelConfigured();

/**
 * Reopens the consent banner, as if no choice had been made.
 * Call this from a "Manage cookies" link.
 */
export const openCookieBanner = (): void => {
  if (!isBrowser) return;
  window.dispatchEvent(new Event(COOKIE_BANNER_EVENT));
};

export const getConsent = (): Consent | null => {
  const value = safeLocalStorage.getItem(CONSENT_KEY);
  return value === 'granted' || value === 'denied' ? value : null;
};

export const setConsent = (granted: boolean): void => {
  const previous = getConsent();
  const value: Consent = granted ? 'granted' : 'denied';
  safeLocalStorage.setItem(CONSENT_KEY, value);

  if (granted) {
    loadGtag();
    loadFacebookPixel();
    trackPageView();
    return;
  }

  // granted -> denied: the tracking scripts are already loaded in this session
  // and can't be unloaded cleanly, so reload the page to stop everything now.
  if (previous === 'granted' && (gtagLoaded || fbqLoaded) && isBrowser) {
    window.location.reload();
  }
};

let gtagLoaded = false;

/**
 * Injects the gtag.js script and initializes GA4. Idempotent.
 */
const loadGtag = (): void => {
  if (!isBrowser || gtagLoaded) return;

  const measurementId = process.env.GATSBY_GOOGLE_ANALYTICS_ID;
  if (!measurementId) return;

  gtagLoaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  // We handle page_view events ourselves (SPA) via trackPageView().
  window.gtag('config', measurementId, { send_page_view: false });
};

let fbqLoaded = false;

/**
 * Injects the Meta (Facebook) Pixel base code and initializes it. Idempotent.
 * Like gtag, the first PageView is sent by trackPageView(), not here, so the
 * SPA route tracking stays the single source of page views.
 */
const loadFacebookPixel = (): void => {
  if (!isBrowser || fbqLoaded) return;

  const pixelId = process.env.GATSBY_FACEBOOK_PIXEL_ID;
  if (!pixelId) return;

  fbqLoaded = true;

  // Meta Pixel base code (queues calls until fbevents.js has loaded).
  if (!window.fbq) {
    const n: any = function (...args: unknown[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    };
    n.queue = [];
    n.loaded = true;
    n.version = '2.0';
    n.push = n;
    window.fbq = n;
    if (!window._fbq) window._fbq = n;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
  }

  window.fbq('init', pixelId);
};

/**
 * Sends a page view. Call on initial load and on each
 * route change (Gatsby does not reload the page for internal navigation).
 */
export const trackPageView = (path?: string): void => {
  if (!isBrowser) return;

  const pagePath = path ?? window.location.pathname + window.location.search;

  if (gtagLoaded && typeof window.gtag === 'function' && process.env.GATSBY_GOOGLE_ANALYTICS_ID) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
    });
  }

  if (fbqLoaded && typeof window.fbq === 'function') {
    window.fbq('track', 'PageView');
  }
};

/**
 * On app startup: if consent was already granted during a previous visit,
 * (re)load the tracking scripts. The initial page view is then emitted by
 * onRouteUpdate -> trackPageView().
 */
export const initAnalytics = (): void => {
  if (getConsent() === 'granted') {
    loadGtag();
    loadFacebookPixel();
  }
};
